// FPD-022 — PATCH /api/finance/processors/{id}/settlement-aging-threshold
// and GET /api/finance/processors/{id}/threshold-impact. The settings page
// renders the affected-row count next to each threshold; the edit modal
// commits + emits the PaymentProcessorThresholdUpdated audit.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ProcessorThresholdImpactDto,
  UpdateProcessorThresholdRequest,
  UpdateProcessorThresholdResult,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson } from './apiFetch';
import { financeQueries, queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';

/** Typed error class carrying the server's stable error code + message. */
export class UpdateProcessorThresholdError extends Error {
  constructor(public code: string, message: string) {
    super(message);
    this.name = 'UpdateProcessorThresholdError';
  }
}

/**
 * FPD-022 — count of in-flight (Reported + null-payout) contributions per
 * processor. Powers the "This affects {N} contributions currently waiting
 * for settlement" subtitle on each settings row.
 */
export function useProcessorThresholdImpact(processorId: string | null | undefined) {
  const { selectedCampaign } = useCampaignStore();
  return useQuery({
    queryKey: queryKeys.finance.processorThresholdImpact(processorId ?? ''),
    queryFn: () =>
      apiJson<ProcessorThresholdImpactDto>(`/api/finance/processors/${processorId}/threshold-impact`, {
        error: 'Failed to fetch threshold impact',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!processorId,
  });
}

export interface UpdateProcessorThresholdVariables {
  processorId: string;
  body: UpdateProcessorThresholdRequest;
}

export function useUpdateProcessorThreshold() {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateProcessorThresholdResult,
    Error,
    UpdateProcessorThresholdVariables
  >({
    mutationFn: ({ processorId, body }) =>
      apiJson<UpdateProcessorThresholdResult>(
        `/api/finance/processors/${processorId}/settlement-aging-threshold`,
        {
          method: 'PATCH',
          json: body,
          error: (error) =>
            new UpdateProcessorThresholdError(
              error.code ?? 'UpdateProcessorThresholdError',
              error.error ?? 'Failed to update processor threshold',
            ),
        },
      ),
    onSuccess: () => {
      // Invalidate the processor list + impact count + downstream finance
      // surfaces that read the threshold (preflight warning text, recon
      // report Section 3 count).
      queryClient.invalidateQueries(financeQueries);
    },
  });
}
